import { useEffect, useRef } from 'react';
import type { VacationRequest } from '../models/vacation.types';
import { canConfirmVacationRequest, resolveDraftGraceEnd } from '../utils/vacationRequestStatus';

export interface UseDraftAutoOpenInput {
    requests: VacationRequest[];
    currentUserEmail: string | null | undefined;
    onOpen: (request: VacationRequest) => void;
}

/**
 * Abre a solicitação quando a carência vence, sem esperar clique. Substitui o job agendado: a
 * escrita é a mesma do botão "Confirmar solicitação", autorizada por `isOwnerConfirmation()`.
 *
 * Só dispara com a página aberta. Como um Draft bloqueia nova solicitação, o dono volta a esta tela
 * e a abertura acontece na montagem.
 */
export function useDraftAutoOpen({
    requests,
    currentUserEmail,
    onOpen,
}: UseDraftAutoOpenInput): void {
    const draft = requests.find((request) => canConfirmVacationRequest(request, currentUserEmail)) ?? null;
    const graceEndMs = draft ? resolveDraftGraceEnd(draft)?.getTime() ?? null : null;
    const attempted = useRef(new Set<string>());

    useEffect(() => {
        if (!draft || graceEndMs === null) return;
        if (attempted.current.has(draft.id)) return;

        const fire = () => {
            if (attempted.current.has(draft.id)) return;
            attempted.current.add(draft.id);
            onOpen(draft);
        };

        const msLeft = graceEndMs - Date.now();
        if (msLeft <= 0) {
            fire();
            return;
        }

        const timer = setTimeout(fire, msLeft);
        return () => clearTimeout(timer);
    }, [draft, graceEndMs, onOpen]);
}
